/**
 * Where a floating panel has to be drawn so a modal cannot bury it.
 *
 * `Modal` is a native `<dialog>` opened with `showModal()`, which puts it in the
 * browser's **top layer** — a plane that paints above the whole document no
 * matter what z-index anything else claims. A panel portalled to `document.body`
 * therefore ends up *behind* an open dialog and its backdrop, which is what a
 * dropdown inside a modal looked like: the field opened, and nothing appeared.
 *
 * Portalling into the dialog instead is not enough on its own. The dialog is
 * animated, so it carries a `transform`, and a transformed element becomes the
 * containing block for `position: fixed` descendants — the panel would be laid
 * out against the dialog's box rather than the window, and then clipped by the
 * dialog's own rounded overflow.
 *
 * So a panel inside a dialog does both: it is portalled into the dialog, which
 * keeps it out of the inert half of the document and inside the field's own
 * React tree, and it promotes itself into the top layer as a popover, which
 * puts the window back as its containing block and lifts it above the dialog.
 *
 * Everywhere else — the ordinary case, a menu on a page — nothing changes: the
 * panel is portalled to `document.body` exactly as before.
 */

/**
 * Whether this browser can promote an element into the top layer on demand.
 *
 * Where it cannot, a panel inside a dialog falls back to the old behaviour
 * rather than to a worse one — portalled to the body, drawn under the dialog.
 */
const SUPPORTS_POPOVER =
  typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;

export interface FloatingHost {
  /** The element the panel is portalled into. */
  container: HTMLElement;
  /** Whether it must lift itself into the top layer to be seen at all. */
  needsTopLayer: boolean;
}

/** Where the panel anchored to `anchor` should be drawn. */
export function floatingHostFor(anchor: Element | null | undefined): FloatingHost {
  const dialog = anchor?.closest('dialog');

  if (SUPPORTS_POPOVER && dialog instanceof HTMLDialogElement && dialog.open) {
    return { container: dialog, needsTopLayer: true };
  }

  return { container: document.body, needsTopLayer: false };
}

/**
 * Lifts a panel into the top layer, once, as it mounts.
 *
 * Called from the panel's own ref callback rather than from an effect so the
 * promotion happens before the browser paints — a panel that appeared under the
 * dialog for a frame and then jumped above it would read as a flicker.
 */
export function promoteToTopLayer(node: HTMLElement | null): void {
  if (!node || !SUPPORTS_POPOVER || node.hasAttribute('popover')) return;

  // Set here rather than as a JSX attribute: React 18's types do not know the
  // attribute yet, and a cast to reach it would be worse than one setAttribute.
  node.setAttribute('popover', 'manual');
  node.showPopover();
}
